/* -----------------------------------------------------------------------------------------------------
  - Created: [11-03-2025] by [SREERAJ]
  - Modified: [2025-06-19]
  - Description: This file contains the Reset Password Form (without current password)
------------------------------------------------------------------------------------------------------- */

import React, { useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { VscEyeClosed } from 'react-icons/vsc';
import { RxEyeOpen } from 'react-icons/rx';
import { Grid } from '@mui/joy';
import PasswordStrengthBar from 'react-password-strength-bar';
import CustomTextFieldWithIcon from '../../../../Components/custom_components/custom_text_field/CustomTextFieldWithIcon';
import ActionButton from '../../../../Components/custom_components/CustomButtons/ActionButton/ActionButton';
import { ErrorToast, SuccessToast } from '../../../../utils/toast';
import { Password_reset } from '../../../../config/ProfileContextApis/resetPasswordApi';
import check_mark_icon from '../../../../Assets/CommonImages/check_mark_icon.png'
import check_mark_grey from '../../../../Assets/CommonImages/check_mark_grey.png'

function PasswordReset({ userId, handleClose }) {

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    const formik = useFormik({
        initialValues: {
            newPassword: '',
            password: '',
        },
        validationSchema: Yup.object({
            newPassword: Yup.string()
                .required('New Password is required')
                .min(8, 'Password must be at least 8 characters')
                .max(20, 'Password must not exceed 20 characters')
                .matches(/[a-z]/, 'Must include at least one lowercase letter')
                .matches(/[A-Z]/, 'Must include at least one uppercase letter')
                .matches(/[0-9]/, 'Must include at least one number')
                .matches(/[@$!%*#?&]/, 'Must include at least one special character'),
            password: Yup.string()
                .required('Confirm Password is required')
                .oneOf([Yup.ref('newPassword')], 'Passwords must match'),
        }),
        onSubmit: async (values) => {
            const params = {
                userId: userId || '',
                password: values.password,
            };
            try {
                setIsButtonDisabled(true);
                const response = await Password_reset(params);
                if (response?.data?.status === 'success') {
                    SuccessToast(response?.data?.message);
                    handleClose(false, '');
                } else {
                    ErrorToast(response?.message || 'Failed to reset password');
                }
            } catch (error) {
                ErrorToast('Something went wrong');
            } finally {
                setIsButtonDisabled(false);
            }
        },
    });

    const conditions = [
        { text: 'Password must be at least 8 characters', isValid: formik.values.password.length >= 8 },
        { text: 'Must contain at least one lowercase letter', isValid: /[a-z]/.test(formik.values.password) },
        { text: 'Must contain at least one uppercase letter', isValid: /[A-Z]/.test(formik.values.password) },
        { text: 'Must contain at least one digit', isValid: /[0-9]/.test(formik.values.password) },
        { text: 'Must contain at least one special character', isValid: /[@$!%*#?&]/.test(formik.values.password) },
    ];

    return (
        <Grid container spacing={3} alignItems="center" justifyContent="center">
            <Grid item xs={12} sm={8} md={6} lg={12}>
                <div className="reset-password-box">

                    {/* New Password Field */}
                    <div className="input-container">
                        <span className='reset-form-label'>New Password</span>
                        <CustomTextFieldWithIcon
                            type={showPassword ? 'text' : 'password'}
                            name="newPassword"
                            placeholder="Enter new password"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.newPassword}
                            EndIcon={showPassword ? RxEyeOpen : VscEyeClosed}
                            onEndIconClick={() => setShowPassword(!showPassword)}
                            error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                            helperText={formik.touched.newPassword && formik.errors.newPassword}
                        />
                        <PasswordStrengthBar password={formik.values.newPassword} />
                    </div>

                    {/* Confirm Password Field */}
                    <div className="input-container">
                        <span className='reset-form-label'>Confirm Password</span>
                        <CustomTextFieldWithIcon
                            type={showConfirmPassword ? 'text' : 'password'}
                            name="password"
                            placeholder="Confirm new password"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.password}
                            EndIcon={showConfirmPassword ? RxEyeOpen : VscEyeClosed}
                            onEndIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            error={formik.touched.password && Boolean(formik.errors.password)}
                            helperText={formik.touched.password && formik.errors.password}
                        />
                    </div>

                    {/* Password Requirements */}
                    <div>
                        {conditions.map((condition, index) => (
                            <div className="password-info-section" key={index}>
                                <img
                                    src={condition.isValid ? check_mark_icon : check_mark_grey}
                                    alt="status_icon"
                                    style={{ width: 20 }}
                                />
                                <span className="password-info-text">{condition.text}</span>
                            </div>
                        ))}
                    </div>

                    {/* Buttons */}
                    <div className="form-bottom-action-buttons-container">
                        <ActionButton
                            onClick={() => formik.resetForm()}
                            label='Reset'
                            btnType='outlined'
                        />
                        <ActionButton
                            onClick={formik.handleSubmit}
                            btnDisable={isButtonDisabled}
                            label='Save'
                            btnType='primary'
                        />
                    </div>
                </div>
            </Grid>
        </Grid>
    );
}

export default PasswordReset;
